import type { z } from 'zod';

/**
 * The UI control used to render a field. Deliberately small: every widget here is a
 * plain HTML input the web app knows how to draw. Add a widget only when a form
 * genuinely needs a new kind of control.
 *
 * `tel` is a text input that the renderer tidies into E.164 the moment the user leaves
 * it, so what they see is what the schema will store. Pair it with `optionalPhone()`.
 *
 * `directory-user` is an email box that also searches the signed-in user's own tenant
 * directory as they type and offers matches to pick from; the value is still the
 * username they end up with. When search is unavailable it is a plain email box.
 *
 * `list` is a table of rows, each row a small set of boxes described by `columns`,
 * with rows added by hand or from a CSV file. Its value is an array of objects.
 */
export type Widget =
  | 'text'
  | 'email'
  | 'tel'
  | 'textarea'
  | 'select'
  | 'date'
  | 'checkbox'
  | 'directory-user'
  | 'list';

/** The size and wording of a `list` field. */
export interface ListOptions {
  /** Fewest rows a submission may carry. */
  min: number;
  /** Most rows a submission may carry. A form limit, not a size limit. */
  max: number;
  /** What one row is called in buttons and messages, e.g. `'person'`. */
  rowLabel: string;
  /**
   * The columns that identify a row — an email address, say — so an uploaded file can
   * be merged rather than appended: a row already present and unchanged is left alone,
   * one present but different is updated, a new one is added. Absent, every upload
   * appends, and only an exact duplicate is skipped.
   */
  key?: readonly string[];
}

export interface SelectOption {
  value: string;
  label: string;
}

/**
 * Presentation hints for one field. This carries *how to show* a field — label,
 * help text, which widget — and never validation rules. Validation lives in the Zod
 * schema so the browser and the server cannot disagree about what is valid.
 *
 * `name` is constrained to the schema's own keys, so a typo in a hint is a compile
 * error rather than a blank field at runtime.
 */
/**
 * Everything a hint says except which key it is for. Split out so that a hint typed for
 * one schema (`FieldHint<Input>`) and a hint for any key (`AnyHint`) are compared
 * structurally: TypeScript otherwise treats two instantiations of one generic as
 * incompatible by variance, and a list's column hints could never be handed to layout.
 */
export interface HintBase {
  label: string;
  widget: Widget;
  helpText?: string;
  placeholder?: string;
  /**
   * Heading this field sits under. Consecutive fields sharing a section render as one
   * group beneath a single heading; omit it and the field renders with no heading.
   * Because grouping is by consecutive run, a form's fields must be ordered so each
   * section is contiguous — the registry tests enforce that.
   */
  section?: string;
  /**
   * Show this field only while another field holds a particular value. The named field
   * must be a `select` over an enum and must appear *earlier* in `fields` than anything
   * it governs; the registry tests enforce both.
   *
   * A hidden field is not rendered and does not reach the payload at all, so its schema
   * must be optional and any "required when visible" rule belongs in the schema.
   */
  visibleWhen?: { field: string; equals: string };
  /** Required for `select`; ignored by other widgets. */
  options?: readonly SelectOption[];
  /**
   * What the control shows before the user touches it. For a `select` it must be one of
   * `options`; the registry tests enforce that. Use it where one answer is nearly always
   * right (a country, a language) so the common case needs no click — never to smuggle
   * in an answer the submitter should have to make.
   */
  defaultValue?: string;
  /**
   * For a `list` field: the hints for one row's boxes, in column order. Their names
   * must be exactly the row schema's keys; the registry tests enforce that, and
   * `listOf()` makes it so by construction. No nested lists.
   */
  columns?: readonly AnyHint[];
  /** For a `list` field: how many rows, and what a row is called. */
  list?: ListOptions;
}

/**
 * Presentation hints for one field of a particular schema. `name` is constrained to the
 * schema's own keys, so a typo in a hint is a compile error rather than a blank field
 * at runtime.
 */
export interface FieldHint<TInput> extends HintBase {
  name: Extract<keyof TInput, string>;
}

/** A hint for any key: what layout code, receipts and column lists work with. */
export interface AnyHint extends HintBase {
  name: string;
}

/**
 * Who a form is for.
 *
 * `'all-customers'` means every allowlisted tenant; a list means only the tenants whose
 * customer number is one of those. It is deliberately required on every form: a form
 * that forgot to say would otherwise default to being visible to everyone, which is the
 * one mistake in this area that fails open.
 */
export type FormAudience = 'all-customers' | readonly string[];

/**
 * Where a submission goes.
 *
 * `'cases'` (the default when absent) files a Dynamics case through the gateway, with the
 * whole submission as JSON in the case description. `'automation'` sends the submission
 * as JSON to the internal app's ingestion endpoint (`AUTOMATION_SUBMISSIONS_URL` on the
 * server) for automated processing, and still files a case — a short plain-text pointer
 * telling the technician to review and approve the request on the service dashboard.
 * The submitter's receipt is the same either way.
 */
export type FormDestination = 'cases' | 'automation';

/** Any Zod object schema. Forms are always objects: one key per field. */
export type AnyFormSchema = z.ZodObject<z.ZodRawShape>;

/**
 * A complete form: identity, the schema that validates it, and the hints to render it.
 *
 * `id` and `version` travel on the wire with every submission so the receiving side
 * knows exactly which shape it was validated against. Bump `version` (semver) whenever
 * the schema changes: minor for additive changes, major for breaking ones.
 */
export interface FormDefinition<S extends AnyFormSchema = AnyFormSchema> {
  id: string;
  version: string;
  title: string;
  description?: string;
  /**
   * Who may use this form: `'all-customers'`, or the Dynamics customer numbers it
   * belongs to, e.g. `['1048']`. List several to share one form between customers.
   *
   * The same number the tenant's cases are filed under, so there is exactly one fact
   * per tenant to configure. This is only half of an entitlement: which *tenant* has
   * which customer number is decided server-side, and deliberately never ships to the
   * browser. A customer number is not a secret — the form id usually contains it — but
   * the mapping from Entra tenant to customer is, so it lives with the tenant allowlist.
   */
  customers: FormAudience;
  /**
   * Where submissions go: a case through the gateway (`'cases'`, the default) or the
   * internal app's automation endpoint plus a pointer case (`'automation'`). Set it when
   * the form becomes a candidate for automation; nothing else about the form changes.
   */
  destination?: FormDestination;
  schema: S;
  fields: readonly FieldHint<z.input<S>>[];
  /**
   * A short subject for the case a submission creates, e.g. the new hire's name. The
   * server composes the case title as `<title> - <caseTitle(payload)>`, so without this
   * every case from a form shares one title and a queue of them cannot be told apart.
   * Optional: the form title alone is used when absent. Given the *validated* payload.
   */
  caseTitle?: (payload: z.output<S>) => string;
}

/**
 * Identity helper that exists purely for type inference: passing the definition
 * through it lets TypeScript infer `S` from `schema` and then check `fields[].name`
 * against it.
 */
export function defineForm<S extends AnyFormSchema>(
  definition: FormDefinition<S>,
): FormDefinition<S> {
  return definition;
}
